import { useEffect, useState } from 'react';

/**
 * Theme-aware chart palette. Reads `--chart-1..N` CSS vars from <html> and
 * re-reads when the theme changes (class / data-theme / style attribute).
 */
export function useThemeChartColors(count = 8): string[] {
  const [colors, setColors] = useState<string[]>([]);

  useEffect(() => {
    const fallback = [
      '#3b82f6',
      '#8b5cf6',
      '#ec4899',
      '#f59e0b',
      '#22c55e',
      '#06b6d4',
      '#f43f5e',
      '#a855f7',
    ];
    const read = () => {
      const root = document.documentElement;
      const out: string[] = [];
      for (let i = 1; i <= count; i++) {
        const c = getComputedStyle(root).getPropertyValue(`--chart-${i}`).trim();
        if (c) out.push(c);
      }
      setColors(out.length ? out : fallback);
    };
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style'],
    });
    return () => obs.disconnect();
  }, [count]);

  return colors;
}

export interface DrillColumn<T> {
  key: keyof T & string;
  header: string;
  align?: 'left' | 'right';
  render?: (value: T[keyof T & string], row: T) => React.ReactNode;
}

export function DrillTable<T extends object>({
  rows,
  columns,
}: {
  rows: T[];
  columns: DrillColumn<T>[];
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-content-muted">
        No records for this slice.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((c) => (
              <th
                key={c.key}
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted"
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b border-border/60 hover:bg-surface-card-hover"
            >
              {columns.map((c) => {
                const v = row[c.key];
                return (
                  <td
                    key={c.key}
                    className={`px-3 py-2 ${
                      c.align === 'right' ? 'text-right' : ''
                    }`}
                  >
                    {c.render ? c.render(v, row) : (v as React.ReactNode) ?? '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
